import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, Building2, Globe2 } from "lucide-react";
import { LogoMark } from "./Logo";

const HERO_IMG =
  "https://images.pexels.com/photos/39192313/pexels-photo-39192313.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const STATS = [
  { icon: BadgeCheck, value: "98%", label: "Visa Success Rate" },
  { icon: Globe2, value: "150+", label: "University Partners" },
  { icon: Building2, value: "12+", label: "India Campus Alliances" },
];

const LINES = [
  <>Architecting Global</>,
  <>Futures in <em className="italic text-amber-600">Education</em></>,
  <>&amp; Institutional Excellence</>,
];

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const panelRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: panelRef, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);

  return (
    <section id="home" data-testid="hero-section" className="pt-28 lg:pt-36 pb-16 lg:pb-24 bg-[#F8FAFC] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE }}
          className="flex items-center gap-3"
        >
          <LogoMark className="w-6 h-6" />
          <span data-testid="hero-eyebrow" className="text-xs font-semibold uppercase tracking-[0.22em] text-amber-800">
            Orion Edcon Services · Official Advisory
          </span>
        </motion.div>

        <h1 className="font-heading font-bold tracking-tight text-[#0B192C] text-5xl sm:text-7xl lg:text-[6.2rem] leading-[0.98] mt-8">
          {LINES.map((line, i) => (
            <MaskedLine key={i} delay={0.18 + i * 0.14}>{line}</MaskedLine>
          ))}
        </h1>

        <div className="grid lg:grid-cols-2 gap-8 mt-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            One-stop support for students bound for the USA, UK, Germany and Latvia — and strategic governance advisory
            for institutions building campuses, partnerships and twinning arrangements in India.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.82, ease: EASE }}
            className="flex flex-wrap gap-4 lg:justify-end"
          >
            <a href="#destinations" data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-[#0B192C] text-white font-semibold px-7 py-3.5 hover:bg-amber-600 transition-colors duration-300">
              Explore Destinations <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a href="#advisory" data-testid="hero-advisory-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B192C] text-[#0B192C] font-semibold px-7 py-3 hover:bg-[#0B192C] hover:text-white transition-colors duration-300">
              Institutional Advisory
            </a>
          </motion.div>
        </div>

        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          className="relative mt-14"
        >
          <div className="relative rounded-2xl overflow-hidden border border-slate-900/10 h-[52vh] lg:h-[64vh]">
            <motion.img
              src={HERO_IMG}
              alt="Graduates celebrating outside a classical university building"
              style={{ y: imgY }}
              className="absolute inset-0 w-full h-[118%] object-cover"
            />
          </div>
          <div className="absolute -top-7 right-4 sm:right-8 flex gap-2.5 sm:gap-3">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: -14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.25 + i * 0.12, ease: EASE }}
                data-testid={`hero-stat-${i}`}
                className="bg-white/95 backdrop-blur rounded-xl border border-slate-900/10 shadow-lg px-3 sm:px-5 py-3 sm:py-4 text-center"
              >
                <s.icon className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 mx-auto" />
                <p className="font-heading text-xl sm:text-2xl font-bold text-[#0B192C] mt-1">{s.value}</p>
                <p className="text-[10px] sm:text-[11px] font-medium text-slate-500 leading-tight">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
